import { merchantIcons } from '@assets/icons';
import { memo, useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

const MerchantConfirmModal = memo(
  ({
    open,
    onClose,
    onConfirm,
    onConfirmStart,
    title,
    description,
    confirmLabel = 'Confirm',
    pendingLabel,
    cancelLabel = 'Cancel',
    danger = true,
    singleAction = false,
    singleActionLabel = 'OK',
    usePortal = true,
    lockBodyScroll = true,
    overlayClassName = '',
  }) => {
    const [submitting, setSubmitting] = useState(false);
    const titleId = useId();

    useEffect(() => {
      if (!open) {
        setSubmitting(false);
        return undefined;
      }

      setSubmitting(false);
      return undefined;
    }, [open]);

    useEffect(() => {
      if (!open) return undefined;

      const handleEscape = (event) => {
        if (event.key !== 'Escape') return;
        if (submitting) return;
        onClose?.();
      };

      document.addEventListener('keydown', handleEscape);
      if (lockBodyScroll) document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleEscape);
        if (lockBodyScroll) document.body.style.overflow = '';
      };
    }, [lockBodyScroll, onClose, open, submitting]);

    const handleClose = useCallback(() => {
      if (submitting) return;
      onClose?.();
    }, [onClose, submitting]);

    const handleConfirm = useCallback(async () => {
      if (submitting) return;
      onConfirmStart?.();
      setSubmitting(true);
      try {
        await onConfirm?.();
      } catch {
        // Caller owns error toasts; keep the modal open so the user can retry or cancel.
      } finally {
        setSubmitting(false);
      }
    }, [onConfirm, onConfirmStart, submitting]);

    if (!open) return null;

    const confirmClass = danger ? 'bg-[#dc2626]' : 'bg-merchant-primary';

    const modal = (
      <div
        className={`${usePortal ? 'fixed' : 'absolute'} inset-0 z-80 flex items-center justify-center p-3 desktop:p-4 animate-fade-in ${overlayClassName}`}
      >
        <button
          type="button"
          className="absolute inset-0 cursor-default border-0 bg-[rgba(17,24,39,0.55)] p-0"
          onClick={handleClose}
          aria-label="Close confirmation dialog"
          disabled={submitting}
        />

        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="relative flex w-full max-w-onboarding flex-col overflow-hidden rounded-field border border-merchant-border bg-white animate-slide-up"
        >
          <div className="relative flex flex-col gap-1.5 desktop:gap-2 border-b border-merchant-border p-3 desktop:p-4">
            <h2
              id={titleId}
              className="pr-8 desktop:text-[24px] text-[20px] font-semibold leading-6 desktop:leading-8 text-merchant-text-primary"
            >
              {title}
            </h2>

            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="absolute top-2 right-2 flex size-5 desktop:size-6 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 outline-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Close"
            >
              <img
                src={merchantIcons.closeCircle}
                alt=""
                className="size-full"
                aria-hidden="true"
              />
            </button>

            <p className="desktop:text-[16px] text-sm font-medium leading-4.5 desktop:leading-6 text-merchant-text-secondary">
              {description}
            </p>
          </div>

          <div className="flex items-start justify-end gap-2.25 desktop:gap-3 border-t border-merchant-border p-3 desktop:p-4">
            {singleAction ? (
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="desktop:min-h-11 min-h-8 desktop:min-w-24 min-w-20 cursor-pointer rounded-full bg-merchant-primary desktop:px-6 desktop:py-3 px-4 py-2 desktop:text-[14px] text-[12px] font-semibold leading-5 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {singleActionLabel}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  className="desktop:min-h-11 min-h-8 desktop:min-w-24 min-w-20 cursor-pointer rounded-full bg-merchant-text-primary px-4.5 py-2 desktop:px-6 desktop:py-3 desktop:text-[14px] text-[12px] font-semibold leading-3.75 desktop:leading-5 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={submitting}
                  className={`desktop:min-h-11 min-h-8 desktop:min-w-24 min-w-20 cursor-pointer rounded-full px-4.5 py-2 desktop:px-6 desktop:py-3 desktop:text-[14px] text-[12px] font-semibold leading-3.75 desktop:leading-5 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 ${confirmClass}`}
                >
                  {submitting && pendingLabel ? pendingLabel : confirmLabel}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );

    return usePortal ? createPortal(modal, document.body) : modal;
  },
);

MerchantConfirmModal.displayName = 'MerchantConfirmModal';

export default MerchantConfirmModal;
